import { useState, useEffect, useRef } from 'react'
import axios from 'axios'

const API = process.env.REACT_APP_API_URL

function formatoCOP(valor) {
  if (valor === 0) return 'Gratis'
  return '$' + valor.toLocaleString('es-CO') + ' COP'
}

/*
  Pantalla de pago. Se usa en dos modos:

  1) Modo normal (justo despues de elegir un plan pago y loguearse): pide
     al backend los datos de la transaccion y monta el boton del Widget de
     Wompi, donde el estudiante elige tarjeta, PSE, Nequi o Daviplata.

  2) Modo confirmacion (modoConfirmacion=true): se usa cuando el estudiante
     vuelve del banco (por ejemplo, tras pagar con PSE, Wompi redirige de
     vuelta a la app). Aqui solo consultamos el estado de la suscripcion
     cada pocos segundos hasta que el webhook de Wompi la haya activado.
*/
export default function Checkout({ usuario, plan, modoConfirmacion, idTransaccion, onListo, onVolver }) {
  const [estado, setEstado] = useState(modoConfirmacion ? 'confirmando' : 'cargando')
  const [error,  setError]  = useState('')
  const [datosTransaccion, setDatosTransaccion] = useState(null)
  const contenedorRef = useRef(null)

  // ── Modo normal: pedir los datos de la transaccion. El widget se monta
  //    en un efecto aparte (mas abajo), una vez que React ya haya pintado
  //    el <div ref={contenedorRef}> — si se intenta montar el widget en
  //    este mismo paso, contenedorRef.current todavia es null porque ese
  //    div solo existe cuando estado === 'listo', y eso pasa un render
  //    despues. ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (modoConfirmacion) return
    if (!usuario?.id || !plan?.id) return
    let cancelado = false

    axios.post(`${API}/pagos/crear-transaccion`, { usuario_id: usuario.id, plan_id: plan.id })
      .then(res => {
        if (cancelado) return
        setDatosTransaccion(res.data)
        setEstado('listo')
      })
      .catch(err => {
        if (cancelado) return
        setError(err.response?.data?.error || 'No pudimos iniciar el pago. Intenta de nuevo.')
        setEstado('error')
      })

    return () => { cancelado = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usuario, plan, modoConfirmacion])

  // Este efecto SI corre despues de que React pinta el <div ref={contenedorRef}>
  // (porque depende de "estado", y ese div solo aparece cuando estado === 'listo'),
  // asi que aqui contenedorRef.current ya existe de verdad.
  useEffect(() => {
    if (estado !== 'listo' || !datosTransaccion) return
    montarBotonWompi(datosTransaccion)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estado, datosTransaccion])

  function montarBotonWompi(datos) {
    if (!contenedorRef.current) return
    contenedorRef.current.innerHTML = ''
    const script = document.createElement('script')
    script.src = 'https://checkout.wompi.co/widget.js'
    script.setAttribute('data-render', 'button')
    script.setAttribute('data-public-key', datos.public_key)
    script.setAttribute('data-currency', datos.moneda)
    script.setAttribute('data-amount-in-cents', String(datos.monto_centavos))
    script.setAttribute('data-reference', datos.referencia)
    script.setAttribute('data-signature:integrity', datos.firma)
    // Si el metodo requiere salir del sitio (PSE, por ejemplo), Wompi
    // regresa aqui con ?pago=<referencia> para que la app pueda confirmar.
    script.setAttribute('data-redirect-url', `${window.location.origin}${window.location.pathname}?pago=${datos.referencia}`)
    if (usuario?.email) script.setAttribute('data-customer-data:email', usuario.email)
    contenedorRef.current.appendChild(script)
  }

  // ── Modo confirmacion: primero intentamos verificar directamente con
  //    Wompi el id de la transaccion (esto funciona aunque el backend
  //    todavia este en localhost y Wompi no pueda llamarnos por webhook).
  //    Si eso no confirma nada, seguimos preguntando cada 3s por si el
  //    webhook llega despues. ─────────────────────────────────────────────
  useEffect(() => {
    if (!modoConfirmacion || !usuario?.id) return
    let cancelado = false

    async function verificarDirecto() {
      if (!idTransaccion) return false
      try {
        const res = await axios.get(`${API}/pagos/verificar/${idTransaccion}`)
        return res.data?.datos?.estado === 'APPROVED'
      } catch (err) {
        return false
      }
    }

    async function consultarEstado() {
      const res = await axios.get(`${API}/pagos/estado/${usuario.id}`)
      return res.data?.datos?.suscripcion_estado === 'activa'
    }

    let intentos = 0
    async function ciclo() {
      if (cancelado) return
      intentos++
      try {
        const aprobadoDirecto = intentos === 1 ? await verificarDirecto() : false
        const activo = aprobadoDirecto || await consultarEstado()
        if (cancelado) return
        if (activo) {
          setEstado('aprobado')
          return
        }
      } catch (err) {
        // seguimos intentando en el siguiente ciclo
      }
      if (intentos >= 20) { // ~60 segundos
        setError('Todavia no confirmamos tu pago. Si ya pagaste, dale un momento y vuelve a intentar mas tarde.')
        setEstado('error')
        return
      }
      setTimeout(ciclo, 3000)
    }
    ciclo()

    return () => { cancelado = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modoConfirmacion, usuario, idTransaccion])

  return (
    <div style={{ minHeight: '100vh', background: '#F8FAFF', fontFamily: 'system-ui, sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ maxWidth: 440, width: '100%', background: '#fff', border: '1px solid #E2E6F0', borderRadius: 18, padding: 32, textAlign: 'center' }}>

        {!modoConfirmacion && onVolver && (
          <button onClick={onVolver}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#6B7280', fontFamily: 'inherit', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 6 }}>
            ← Volver
          </button>
        )}

        {plan && !modoConfirmacion && (
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 30, marginBottom: 8 }}>{plan.emoji}</div>
            <div style={{ fontSize: 17, fontWeight: 800, color: '#1A1A2E' }}>{plan.nombre}</div>
            <div style={{ fontSize: 21, fontWeight: 900, color: '#1B3A8C', marginTop: 4 }}>
              {formatoCOP(plan.precio)}{plan.periodo ? ' ' + plan.periodo : ''}
            </div>
          </div>
        )}

        {estado === 'cargando' && (
          <p style={{ color: '#6B7280', fontSize: 14 }}>Preparando tu pago...</p>
        )}

        {estado === 'listo' && (
          <div>
            <p style={{ color: '#6B7280', fontSize: 14, marginBottom: 18 }}>
              Elige como quieres pagar: tarjeta, PSE, Nequi o Daviplata.
            </p>
            <div ref={contenedorRef} />
            <p style={{ fontSize: 11, color: '#9CA3AF', marginTop: 18 }}>
              🔒 El pago lo procesa Wompi directamente. No guardamos los datos de tu tarjeta.
            </p>
          </div>
        )}

        {estado === 'confirmando' && (
          <div>
            <div style={{ fontSize: 36, marginBottom: 12 }}>⏳</div>
            <p style={{ color: '#6B7280', fontSize: 14 }}>Confirmando tu pago con Wompi...</p>
          </div>
        )}

        {estado === 'aprobado' && (
          <div>
            <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
            <h2 style={{ fontSize: 19, fontWeight: 800, color: '#1A1A2E', marginBottom: 8 }}>¡Pago aprobado!</h2>
            <p style={{ color: '#6B7280', fontSize: 14, marginBottom: 22 }}>Tu plan ya esta activo.</p>
            <button onClick={onListo}
              style={{ width: '100%', padding: '12px', background: '#1B3A8C', color: '#fff', border: 'none', borderRadius: 10, fontFamily: 'inherit', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
              Ir a la plataforma
            </button>
          </div>
        )}

        {estado === 'error' && (
          <div>
            <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
            <p style={{ color: '#991B1B', fontSize: 14, marginBottom: 20 }}>{error}</p>
            <button onClick={onListo}
              style={{ width: '100%', padding: '12px', background: 'transparent', color: '#1B3A8C', border: '1.5px solid #E2E6F0', borderRadius: 10, fontFamily: 'inherit', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
              Ir a la plataforma
            </button>
          </div>
        )}

      </div>
    </div>
  )
}
